import * as React from 'react';
import PropTypes from 'prop-types';
import FormField from '../../components/FormField/FormField';
import Button from '../../components/Button/Button';
import TimeInput from '../../components/TimeInput/TimeInput';
import {
  printDurationAsHoursAndMinutes,
  printInNativeTimeInputFormat
} from '../../libs/dateTimePrinting';
import {
  TimesheetEntryItemDelta,
  TimesheetEntryItemFieldAndButtonsWrapper
} from './TimesheetEntryTimeField.style';
import YPTimesheetEntryObject from '../../yesplan_classes/YPTimesheetEntryObject';
import FormatSettingsContext from '../../components/FormatSettingsContext';
import { Body } from '../../styled components/Typography';

class TimesheetEntryTimeField extends React.Component {
  constructor(props) {
    super(props);
    this.changeDateTimeFromTimeInput = this.changeDateTimeFromTimeInput.bind(this);
  }

  setInputElValue(dateTime) {
    const { onChangeFn, inputRef, autosaveTimesheet, processTimeChangeFn } = this.props;
    const timeString = printInNativeTimeInputFormat(dateTime);
    if (inputRef?.current) {
      inputRef.current.value = timeString;
    }
    onChangeFn(dateTime);
    processTimeChangeFn();
    autosaveTimesheet();
  }

  #getIncrementedTime() {
    const { dateTime } = this.props;
    return dateTime.clone().add(15, 'minutes');
  }

  #getDecrementedTime() {
    const { dateTime } = this.props;
    return dateTime.clone().subtract(15, 'minutes');
  }

  incrementTime() {
    const incrementedDateTimeObj = this.#getIncrementedTime();
    this.setInputElValue(incrementedDateTimeObj);
  }

  decrementTime() {
    const decrementedDateTimeObj = this.#getDecrementedTime();
    this.setInputElValue(decrementedDateTimeObj);
  }

  canPerformTimeChange(newTime) {
    return this.props.canPerformTimeChange(newTime);
  }

  changeDateTimeFromTimeInput(timeString) {
    const { dateTime, onChangeFn } = this.props;
    const [hourString, minuteString] = timeString.split(':');
    const currentDateNewTime = dateTime
      .clone()
      .hour(Number.parseInt(hourString))
      .minute(Number.parseInt(minuteString));
    onChangeFn(currentDateNewTime);
  }

  renderIncrementButton() {
    return (
      <Button
        contentType="icon"
        type="button"
        icon="add-circle-outline"
        size="medium"
        disabled={!this.canPerformTimeChange(this.#getIncrementedTime())}
        onClick={(e) => {
          e.preventDefault();
          this.incrementTime();
        }}
      />
    );
  }

  renderDecrementButton() {
    return (
      <Button
        contentType="icon"
        type="button"
        icon="minus-circle-outline"
        size="medium"
        disabled={!this.canPerformTimeChange(this.#getDecrementedTime())}
        onClick={(e) => {
          e.preventDefault();
          this.decrementTime();
        }}
      />
    );
  }

  renderDeltaWhenEditable() {
    const { delta, isCancelled } = this.props;
    if (delta.as('minutes') === 0) return this.renderAsPlannedDelta();
    return (
      <TimesheetEntryItemDelta $notAsPlanned $isCancelled={isCancelled} data-test-delta>
        {printDurationAsHoursAndMinutes(delta)}
      </TimesheetEntryItemDelta>
    );
  }

  renderDeltaWhenReadOnly() {
    const { delta, dateTime, isCancelled } = this.props;
    if (dateTime === null) return null;
    if (delta.as('minutes') === 0) return this.renderAsPlannedDelta();
    return (
      <TimesheetEntryItemDelta $notAsPlanned $isCancelled={isCancelled} data-test-delta>
        {printDurationAsHoursAndMinutes(delta)}
      </TimesheetEntryItemDelta>
    );
  }

  renderAsPlannedDelta() {
    const { isCancelled } = this.props;
    return (
      <TimesheetEntryItemDelta data-test-delta $isCancelled={isCancelled}>
        {i18n('As planned')}
      </TimesheetEntryItemDelta>
    );
  }

  renderDelta() {
    const { canEdit } = this.props;
    if (canEdit) return this.renderDeltaWhenEditable();

    return this.renderDeltaWhenReadOnly();
  }

  renderReadonlyField() {
    const { dateTime, isValidInput, inputRef, timesheetEntryObject } = this.props;
    const { timeFormatSetting } = this.context;
    const timeString = printInNativeTimeInputFormat(dateTime, timeFormatSetting);
    const isShiftEnd = timesheetEntryObject.getShowOnlyStartOrEnd() === 'end';

    return (
      <>
        {isShiftEnd && this.renderDelta()}
        <TimeInput
          tw="mb-0 text-right"
          readOnly
          ref={inputRef}
          value={timeString}
          invalid={!isValidInput}
        />
        {timesheetEntryObject.shouldShowStart() && this.renderDelta()}
      </>
    );
  }

  renderCancelledField() {
    const { dateTime, timesheetEntryObject } = this.props;
    const { timeFormatSetting } = this.context;
    const timeString = printInNativeTimeInputFormat(dateTime, timeFormatSetting);
    const isShiftEnd = timesheetEntryObject.getShowOnlyStartOrEnd() === 'end';

    return (
      <>
        {isShiftEnd && this.renderDelta()}
        <Body $color="black" tw="line-through" data-test-cancelled-time>
          {timeString}
        </Body>
        {timesheetEntryObject.shouldShowStart() && this.renderDelta()}
      </>
    );
  }

  renderEditableField() {
    const {
      isValidInput,
      inputRef,
      dateTime,
      timesheetEntryObject,
      autosaveTimesheet,
      processTimeChangeFn
    } = this.props;
    const { timeFormatSetting } = this.context;
    const timeString = printInNativeTimeInputFormat(dateTime, timeFormatSetting);
    const isShiftEnd = timesheetEntryObject.getShowOnlyStartOrEnd() === 'end';

    return (
      <TimesheetEntryItemFieldAndButtonsWrapper $isShiftEnd={isShiftEnd}>
        {this.renderDecrementButton()}
        <div tw="flex flex-col">
          {isShiftEnd && this.renderDelta()}
          <TimeInput
            tw="mb-0"
            ref={inputRef}
            value={timeString}
            invalid={!isValidInput}
            onChange={(e) => {
              this.changeDateTimeFromTimeInput(e.target.value);
            }}
            onBlur={(e) => {
              processTimeChangeFn();
              autosaveTimesheet();
            }}
          />
          {timesheetEntryObject.shouldShowStart() && this.renderDelta()}
        </div>

        {this.renderIncrementButton()}
      </TimesheetEntryItemFieldAndButtonsWrapper>
    );
  }

  renderField() {
    const { canEdit, isCancelled } = this.props;
    if (isCancelled) return this.renderCancelledField();
    if (canEdit) return this.renderEditableField();
    return this.renderReadonlyField();
  }

  render() {
    const { timesheetEntryObject } = this.props;
    return (
      <FormField
        tw="flex flex-col items-end"
        data-test-timeinput={timesheetEntryObject.getShowOnlyStartOrEnd()}
      >
        {this.renderField()}
      </FormField>
    );
  }
}

TimesheetEntryTimeField.propTypes = {
  inputRef: PropTypes.shape({
    current: PropTypes.shape({})
  }).isRequired,
  dateTime: PropTypes.shape({}).isRequired,
  delta: PropTypes.shape({}).isRequired,
  onChangeFn: PropTypes.func.isRequired,
  processTimeChangeFn: PropTypes.func.isRequired,
  isValidInput: PropTypes.bool,
  canEdit: PropTypes.bool.isRequired,
  isCancelled: PropTypes.bool.isRequired,
  timesheetEntryObject: PropTypes.instanceOf(YPTimesheetEntryObject).isRequired,
  autosaveTimesheet: PropTypes.func.isRequired,
  canPerformTimeChange: PropTypes.func.isRequired
};

TimesheetEntryTimeField.defaultProps = {
  isValidInput: true
};

TimesheetEntryTimeField.contextType = FormatSettingsContext;

export default TimesheetEntryTimeField;
